/**
 * EditProfileSubscriptionSection
 *
 * The "Subscription" card of `EditUserProfilePage` — the monthly exclusive-content
 * subscription charge. Port of Flutter's `subscriptionChargeSection()`, rendered
 * last in the scroll body (`edit_user_profile_screen.dart:472-476`).
 *
 * Extracted verbatim from the page in Task 11 (the page crossed the 300-line
 * ceiling once the profile-image section landed). Presentational and stateless:
 * the value and its mutator are owned by `useEditUserProfile`. The decimal
 * clamping stays here because it is input formatting, not business logic — the
 * hook re-validates the same band before saving.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { InputText } from '@/presentation/components/common/InputText';
import { TextInputFormatters } from '@/presentation/constants/formatters/textInputFormatters';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { useEditProfileSectionStyles } from '../hooks/useEditProfileSectionStyles';

export interface EditProfileSubscriptionSectionProps {
  subscriptionCharge: string;
  onSubscriptionChargeChange: (value: string) => void;
}

export const EditProfileSubscriptionSection: React.FC<EditProfileSubscriptionSectionProps> = ({
  subscriptionCharge,
  onSubscriptionChargeChange,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const { sectionHeaderStyle, cardStyle, rowStyle } = useEditProfileSectionStyles();

  return (
    <div style={{ marginBottom: responsive.spacing.lg }}>
      <p style={sectionHeaderStyle}>
        {l10n.editUserProfile.subscriptionSection}
      </p>
      <div style={cardStyle}>
        <div style={rowStyle}>
          <InputText
            qa={{ id: 'edit-profile-subscription-charge' }}
            multiline={false}
            inputMode="decimal"
            value={subscriptionCharge}
            onChange={(v) =>
              onSubscriptionChargeChange(
                TextInputFormatters.decimalAmount(
                  v,
                  subscriptionCharge,
                  // Flutter: `RemoteConfigService.exclusiveSubscriptionMinDollars` /
                  // `.exclusiveSubscriptionMaxDollars` (edit_user_profile_screen.dart:328-335).
                  // Read inside the change handler, so a template activating mid-session
                  // applies to the next keystroke.
                  remoteConfigService.exclusiveSubscriptionMinDollars,
                  remoteConfigService.exclusiveSubscriptionMaxDollars
                )
              )
            }
            placeholder={l10n.editUserProfile.subscriptionChargePlaceholder}
            backgroundColor={theme.transparent}
            textColor={theme.text.primary}
            fontSize={responsive.font.lg}
            style={{ flex: 1, minWidth: 0 }}
          />
        </div>
      </div>
      <p style={{
        color: theme.text.secondary,
        fontSize: responsive.font.xs,
        marginTop: responsive.spacing.xs,
        paddingLeft: responsive.spacing.xs
      }}>
        {l10n.editUserProfile.subscriptionDescription}
        {l10n.editUserProfile.subscriptionDescriptionEditing}
      </p>
    </div>
  );
};
